import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Message, MessagingService } from '@ca/core/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Guid } from 'guid-typescript';
import { EMPTY, Observable, Subject, from, iif, merge, of, timer } from 'rxjs';
import { catchError, map, share, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DatasetVM } from 'src/app/features/ml-annotate/models/dataset-vm.model';
import { CziConversionDialogComponent } from 'src/app/shared/components/czi-conversion-dialog/czi-conversion-dialog.component';
import { CZI_EXTENSION } from 'src/app/shared/constants/constants';
import { CZI_CONVERSION_DONE_NEXT_ADD_IMAGES_TO_DATASET, CZI_CONVERSION_RETRY } from 'src/app/shared/constants/czi-conversion-messages';
import * as FileUtils from '../../../utils/file/file-utils';
import { Dataset, DatasetFiles, FileModel } from '../models';
import { FileConversionStatus } from '../models/file-conversion-status.model';
import { MessagingCallbackAction } from '../models/messaging-topic-action.model';
import { DataService } from './abstract-services/data.service';
import { DialogService } from './dialog/dialog.service';
import { ExperimentalFeaturesService } from './experimental-features.service';
import { MachineLearningService } from './machine-learning.service';

@Injectable({ providedIn: 'root' })
export class CziConverterService {
    private readonly apiUrl = '/api/documents/convert';
    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    private conversions = new Map<
        string,
        {
            conversionPollingTimer$: Observable<number> | null;
            stopConversionPolling$$: Subject<boolean>;
            fileIdsUnderConversion: Set<string>;
        }
    >();

    constructor(
        private http: HttpClient,
        private dialogService: DialogService,
        private translateService: TranslateService,
        private machineLearningService: MachineLearningService,
        private modalService: NgbModal,
        private messagingService: MessagingService,
        private experimentalFeaturesService: ExperimentalFeaturesService,
    ) {}

    convertFileToCziIfNotCziAndStartPolling$(
        operationId: string,
        selectedFilesParam: FileModel[],
        file: FileModel,
        datasetId?: string | null,
        apeerFilesMap?: Map<string, FileModel>,
        isMedCZIConversionDisabled?: boolean | undefined,
        isSegmentation: boolean = false,
    ): Observable<FileModel[]> {
        let selectedFiles = [...selectedFilesParam];

        if (!this.experimentalFeaturesService.isCziConversionFeatureEnabled || isMedCZIConversionDisabled) {
            return of(selectedFilesParam);
        }
        if (this.fileIsNotUnderConversion(operationId, file)) {
            if (FileUtils.getFileExtension(file.name) === CZI_EXTENSION) {
                selectedFiles = selectedFilesParam.map(f => {
                    if (f.id === file.id) {
                        return Object.assign(f, { convertStatus: 'Success' });
                    }
                    return f;
                });
            } else {
                selectedFiles = selectedFilesParam.map(f => {
                    if (f.id === file.id) {
                        return Object.assign(f, { convertStatus: 'Active' });
                    }
                    return f;
                });
                if (!this.conversions.get(operationId)) {
                    this.conversions.set(operationId, {
                        conversionPollingTimer$: null,
                        stopConversionPolling$$: new Subject(),
                        fileIdsUnderConversion: new Set<string>(),
                    });
                }

                return this.convertFile$(file.id, isSegmentation && this.isMEDProjectEnabled ? null : datasetId ? datasetId : null).pipe(
                    switchMap(cziFileId => {
                        const conversion = this.conversions.get(operationId);
                        if (conversion) {
                            conversion.fileIdsUnderConversion.add(cziFileId);
                        }
                        const index = selectedFiles.findIndex(f => f.id === file.id);
                        if (index !== -1) {
                            // In case that conversion produces new file, this line will change file id to the new one.
                            // So, polling will happen without problems, cause we will ask for the status of the new file.
                            // In case that cziFileId stays the same, this line actually won't make changes to the selectedFiles,
                            // and we will have one file with the original id and ask for the status of that file.
                            selectedFiles[index].id = cziFileId;
                        }
                        return this.updateOperationFileStatuses$(operationId, selectedFiles, apeerFilesMap);
                    }),
                );
            }
        }
        return this.updateOperationFileStatuses$(operationId, selectedFiles, apeerFilesMap);
    }

    updateOperationFileStatuses$(
        operationId: string,
        selectedFiles: FileModel[],
        apeerFilesMap?: Map<string, FileModel>,
        fileConversionsStatuses?: FileConversionStatus[],
    ): Observable<FileModel[]> {
        if (!this.experimentalFeaturesService.isCziConversionFeatureEnabled) {
            return of(selectedFiles);
        }
        let conversion = this.conversions.get(operationId);
        if (!conversion && fileConversionsStatuses?.find(fc => ['Active', 'Failed', 'Unknown'].includes(fc.status))) {
            this.conversions.set(operationId, {
                conversionPollingTimer$: null,
                stopConversionPolling$$: new Subject(),
                fileIdsUnderConversion: new Set<string>(),
            });
            conversion = this.conversions.get(operationId);
            fileConversionsStatuses.forEach(fc => conversion?.fileIdsUnderConversion.add(fc.currentFileId));
        }

        let updatedSelectedFiles = this.getFilesWithInitialFileStatusesSet(selectedFiles);

        if (!conversion) {
            return of(updatedSelectedFiles);
        }

        conversion.stopConversionPolling$$.next(true);
        if (conversion.conversionPollingTimer$ == null) {
            conversion.conversionPollingTimer$ = timer(500, 5000).pipe(takeUntil(conversion.stopConversionPolling$$));
        }
        return conversion.conversionPollingTimer$.pipe(
            switchMap(() => {
                const cziConversionFileIds = updatedSelectedFiles.reduce((conversionFileIds: string[], sf: FileModel) => {
                    if (conversion?.fileIdsUnderConversion.has(sf.id)) {
                        conversionFileIds.push(sf.id);
                    }
                    return conversionFileIds;
                }, []);
                return iif(
                    () => cziConversionFileIds.length > 0,
                    this.getFilesStatus$(cziConversionFileIds).pipe(
                        catchError((error: unknown) => {
                            console.error(error);
                            return EMPTY;
                        }),
                    ),
                    of(updatedSelectedFiles),
                );
            }),
            tap(filesWithUpdatedStatus => {
                updatedSelectedFiles = updatedSelectedFiles.map(sf => {
                    let file: FileModel | undefined;
                    if (conversion!.fileIdsUnderConversion.has(sf.id)) {
                        file = filesWithUpdatedStatus.find(f => f.id === sf.id);
                    }
                    if (file) {
                        sf.originalFileId = file.originalFileId;
                        sf.convertStatus = file.convertStatus;
                        sf.name = file.name;
                        // we need to check if file size is greater than zero because when conversion is done
                        // some additional time is needed to update record in the document service
                        if (file.sizeInBytes > 0 && file.convertStatus === 'Success') {
                            if (this.isMEDProjectEnabled) {
                                sf.validationStatus = 'Valid';
                            }

                            const originalFileCreatedAt = sf.createdAt;
                            Object.assign(sf, file);
                            // use original createdAt property to avoid the jumping of the file list
                            sf.createdAt = originalFileCreatedAt;
                            // backend returns null for the storage type, but correct storage type is needed in order to preview the file
                            // TODO check if backend can return correct storage type and remove the line below in that case
                            sf.storageType = 'Azure';
                            // if replace and apeerFilesMap is provided then we need to replace apeer file with converted file
                            // to immediately show this change in the file explorer without refreshing it
                            if (apeerFilesMap) {
                                const apeerFile = apeerFilesMap.get(sf.id);
                                if (apeerFile) {
                                    Object.assign(apeerFile, file);
                                    apeerFile.storageType = 'Azure';
                                }
                            }
                        } else {
                            if (file.convertStatus !== 'Unknown') {
                                sf.convertStatus = file.convertStatus === 'Success' && file.sizeInBytes === 0 ? 'Active' : file.convertStatus;
                            }
                            if (file.convertStatus == 'Failed') {
                                sf.id = file.originalFileId ?? file.id;
                            }
                        }
                    }
                    return sf;
                });
                this.stopPolling(filesWithUpdatedStatus, operationId);
            }),
            tap(() => {
                this.stopPolling(updatedSelectedFiles, operationId);
            }),
            switchMap(() => of([...updatedSelectedFiles])),
        );
    }

    clearConversionMap(operationId: string): void {
        if (!this.experimentalFeaturesService.isCziConversionFeatureEnabled) {
            return;
        }
        this.conversions.delete(operationId);
    }

    confirmCziConversionIfNeededAndTriggerCallbackWhenDone$(
        operationId: string,
        datasetOrDatasetFiles: Dataset | { datasetId: string; files: FileModel[] },
        callbackAction: MessagingCallbackAction<Dataset | DatasetVM>,
        currentDatasetName?: string,
    ): Observable<FileModel[]> {
        if (datasetOrDatasetFiles instanceof Dataset) {
            if (
                this.experimentalFeaturesService.isCziConversionFeatureEnabled &&
                datasetOrDatasetFiles.statistics.numberOfCziFiles < datasetOrDatasetFiles.statistics.numberOfFiles
            ) {
                return this.machineLearningService.fetchDatasetContent$(datasetOrDatasetFiles.datasetId).pipe(
                    switchMap((datasetFiles: DatasetFiles) => {
                        if (this.areSomeFilesActiveOrUnknown(datasetFiles.filesConversions)) {
                            this.dialogService.showInfo(
                                {
                                    message: this.translateService.instant('CZI_CONVERSION.CONVERSION_IN_PROGRESS_DATASET_LIST', {
                                        dataset: datasetOrDatasetFiles.name,
                                    }),
                                },
                                true,
                                datasetOrDatasetFiles,
                            );
                            return EMPTY;
                        }
                        return this.confirmAndStartCziConversion$(operationId, datasetFiles.files, datasetOrDatasetFiles.datasetId, callbackAction);
                    }),
                );
            } else {
                this.messagingService.publish(callbackAction.topicId, { action: callbackAction.action, payload: callbackAction.payload });
                return EMPTY;
            }
        } else {
            if (
                !this.experimentalFeaturesService.isCziConversionFeatureEnabled ||
                this.checkIfAllFilesAreCzi(datasetOrDatasetFiles.files as FileModel[]) ||
                (callbackAction.action === CZI_CONVERSION_DONE_NEXT_ADD_IMAGES_TO_DATASET &&
                    this.areSomeFilesActiveOrUnknown(datasetOrDatasetFiles.files as FileModel[]))
            ) {
                this.messagingService.publish(callbackAction.topicId, { action: callbackAction.action, payload: callbackAction.payload });
                return EMPTY;
            } else {
                if (this.areSomeFilesActiveOrUnknown(datasetOrDatasetFiles.files as FileModel[])) {
                    this.dialogService.showInfo({
                        message: this.translateService.instant('CZI_CONVERSION.CONVERSION_IN_PROGRESS_DATASET_DETAIL', {
                            dataset: currentDatasetName,
                        }),
                    });
                    return EMPTY;
                }
                return this.confirmAndStartCziConversion$(
                    operationId,
                    datasetOrDatasetFiles.files as FileModel[],
                    datasetOrDatasetFiles.datasetId,
                    callbackAction,
                );
            }
        }
    }

    areAllFilesAreConvertedOrFailed(files: FileModel[]): boolean {
        if (!this.experimentalFeaturesService.isCziConversionFeatureEnabled) {
            return true;
        }
        return files.every(f => (f.type ? f.type : FileUtils.getFileExtension(f.name)) === CZI_EXTENSION || f.convertStatus === 'Failed');
    }

    areSomeFilesActiveOrUnknown(files: FileModel[] | FileConversionStatus[]): boolean {
        if (!this.experimentalFeaturesService.isCziConversionFeatureEnabled) {
            return false;
        }

        return files.some(f => {
            return f instanceof FileModel
                ? files.some(f => f.convertStatus != null && (f.convertStatus === 'Active' || f.convertStatus === 'Unknown'))
                : files.some(f => f.status != null && (f.status === 'Active' || f.status === 'Unknown'));
        });
    }

    getNumberOfFilesBeingConverted(files: FileModel[]): number {
        if (!this.experimentalFeaturesService.isCziConversionFeatureEnabled) {
            return 0;
        }
        return files.filter(f => f.convertStatus === 'Active' || f.convertStatus === 'Unknown').length;
    }

    getNumberOfConversionFailedFiles(files: FileModel[]): number {
        if (!this.experimentalFeaturesService.isCziConversionFeatureEnabled) {
            return 0;
        }
        return files.filter(f => f.convertStatus != null && f.convertStatus !== 'Success' && f.convertStatus !== 'Active').length;
    }

    removeFileFromConversionMap(operationId: string, fileId: string): void {
        if (!this.experimentalFeaturesService.isCziConversionFeatureEnabled) {
            return;
        }
        const conversion = this.conversions.get(operationId);
        if (conversion) {
            conversion.fileIdsUnderConversion.delete(fileId);
        }
    }

    private fileIsNotUnderConversion(operationId: string, file: FileModel): boolean {
        return !this.conversions.get(operationId)?.fileIdsUnderConversion.has(file.id);
    }

    private convertFile$(fileId: string, datasetId?: string | null): Observable<string> {
        const replaceFlag = this.isMEDProjectEnabled ? `false` : `true`;
        const queryParams = datasetId ? `?replace=${replaceFlag}&datasetId=${datasetId}` : `?replace=${replaceFlag}`;
        return this.http.post<string>(`${this.apiUrl}/${fileId}${queryParams}`, { headers: DataService.plainJsonContentType });
    }

    private getFilesWithInitialFileStatusesSet(files: FileModel[]): FileModel[] {
        const updatedFiles = this.checkIfFileIsCziAndSetSuccessStatus(files);
        return updatedFiles.map(f => {
            if ((f.type ? f.type : FileUtils.getFileExtension(f.name)) !== CZI_EXTENSION && f.convertStatus == null) {
                f.convertStatus = 'NotCZI';
            }
            return f;
        });
    }
    private confirmAndStartCziConversion$(
        operationId: string,
        selectedFiles: FileModel[],
        datasetId: string,
        callbackAction?: MessagingCallbackAction<Dataset | DatasetVM>,
    ): Observable<FileModel[]> {
        if (selectedFiles.length > 0 && selectedFiles.some(f => (f.type ? f.type : FileUtils.getFileExtension(f.name)) !== CZI_EXTENSION)) {
            const modalTitle = this.translateService.instant('CZI_CONVERSION.CONVERT_FILES_TITLE');
            const message = this.translateService.instant('CZI_CONVERSION.CONVERT_FILES_MESSAGE');
            const warningMessage = this.isMEDProjectEnabled ? '' : this.translateService.instant('CZI_CONVERSION.CONVERT_FILES_WARNING');
            const cancelText = this.translateService.instant('CZI_CONVERSION.CONVERT_FILES_CANCEL');
            const confirmText = this.translateService.instant('CZI_CONVERSION.CONVERT_FILES_CONVERT');
            const confirmModal = this.dialogService.openConfirm({
                modalTitle,
                message,
                warningMessage,
                cancelText,
                confirmText,
                options: { size: 'm' },
            });

            const topicId = Guid.create().toString();
            const retryAction: MessagingCallbackAction<FileModel[]> = { action: CZI_CONVERSION_RETRY, topicId };
            return merge(from(confirmModal.result), this.messagingService.subscription(topicId)).pipe(
                map((message: Message<FileModel[]>) => this.prepareFilesForCziConversion((message && message.payload) || selectedFiles)),
                tap(({ notCziFiles }) => {
                    for (let i = 0; i <= notCziFiles.length - 2; i++) {
                        this.convertFileToCziIfNotCziAndStartPolling$(operationId, selectedFiles, notCziFiles[i], datasetId).subscribe();
                    }
                }),
                switchMap(({ notCziFiles, originalSizeMap, overallSizeOfNotCziFiles }) => {
                    const cziConversionModalModal = this.modalService.open(CziConversionDialogComponent);
                    const cziConversionDialogComponent = cziConversionModalModal.componentInstance as CziConversionDialogComponent;
                    cziConversionDialogComponent.initialNotCziFiles = notCziFiles;
                    cziConversionDialogComponent.originalSizeMap = originalSizeMap;
                    cziConversionDialogComponent.overallSizeOfOriginalFiles = overallSizeOfNotCziFiles;
                    cziConversionDialogComponent.datasetId = datasetId;
                    cziConversionDialogComponent.messagingCallbackAction = callbackAction;
                    cziConversionDialogComponent.retryAction = retryAction;
                    const cziConversion$ = this.convertFileToCziIfNotCziAndStartPolling$(
                        operationId,
                        selectedFiles,
                        notCziFiles[notCziFiles.length - 1],
                        datasetId,
                    ).pipe(
                        share(),
                        tap(files => {
                            cziConversionDialogComponent.files = files;
                        }),
                    );
                    return cziConversion$;
                }),
            );
        } else {
            return this.updateOperationFileStatuses$(operationId, selectedFiles);
        }
    }

    private prepareFilesForCziConversion(files: FileModel[]): {
        notCziFiles: FileModel[];
        originalSizeMap: Map<string, number>;
        overallSizeOfNotCziFiles: number;
    } {
        const notCziFiles = files.reduce((filteredNotCziFiles: FileModel[], f: FileModel) => {
            if ((f.type ? f.type : FileUtils.getFileExtension(f.name)) !== CZI_EXTENSION) {
                filteredNotCziFiles.push(f);
            }
            return filteredNotCziFiles;
        }, []);

        const originalSizeMap = new Map<string, number>();
        const overallSizeOfNotCziFiles = notCziFiles.reduce((sum: number, f: FileModel) => {
            sum += f.sizeInBytes;
            originalSizeMap.set(FileUtils.getFileNameWithoutExtension(f.name), f.sizeInBytes);
            return sum;
        }, 0);

        return { notCziFiles, originalSizeMap, overallSizeOfNotCziFiles };
    }

    private checkIfAllFilesAreCzi(files: FileModel[]): boolean {
        return !files.some(f => (f.type ? f.type : FileUtils.getFileExtension(f.name)) !== CZI_EXTENSION);
    }

    private checkIfFileIsCziAndSetSuccessStatus(files: FileModel[]): FileModel[] {
        return files.map(f => {
            // we need to check if file size is greater than zero because when conversion is done
            // some additional time is needed to update record in the document service
            if ((f.type ? f.type : FileUtils.getFileExtension(f.name)) === CZI_EXTENSION && f.sizeInBytes > 0 && f.convertStatus !== 'Success') {
                f.convertStatus = 'Success';
            }
            return f;
        });
    }

    private getFilesStatus$(fileIds: string[]): Observable<FileModel[]> {
        return this.http.post<FileModel[]>(`${this.apiUrl}/status?access=read`, fileIds, { headers: DataService.plainJsonContentType });
    }

    private stopPolling(files: FileModel[], operationId: string): void {
        if (
            files.every(
                f =>
                    (f.sizeInBytes > 0 && f.convertStatus === 'Success') ||
                    f.convertStatus === 'Failed' ||
                    f.convertStatus == null ||
                    f.convertStatus === 'NotCZI',
            )
        ) {
            const conversion = this.conversions.get(operationId);
            if (conversion) {
                conversion.fileIdsUnderConversion.clear();
                setTimeout(() => {
                    conversion.stopConversionPolling$$.next(true);
                });
                conversion.conversionPollingTimer$ = null;
            }
        }
    }
}
